import fs from 'fs-extra';
import path from 'path';
import crypto from 'crypto';
import { WORKFLOW_DIR } from '../../constants.js';
import { logger } from '../../utils/logger.js';

export interface User {
  id: string;
  username: string;
  email?: string;
  avatar?: string;
  role?: string;
  created: string;
  lastLogin?: string;
}

export interface AuthSession {
  token: string;
  userId: string;
  created: string;
  expires: string;
}

export class AuthService {
  private usersPath: string;
  private sessionsPath: string;
  private secretKey: string;

  constructor() {
    this.usersPath = path.join(process.cwd(), WORKFLOW_DIR, 'users.json');
    this.sessionsPath = path.join(process.cwd(), WORKFLOW_DIR, 'sessions.json');
    this.secretKey = process.env.AUTH_SECRET || 'workflow-default-secret-key';
    this.ensureAuthFiles();
  }

  private async ensureAuthFiles() {
    await fs.ensureFile(this.usersPath);
    await fs.ensureFile(this.sessionsPath);
    
    // 创建默认用户
    if (!(await fs.pathExists(this.usersPath)) || (await fs.readFile(this.usersPath, 'utf-8')).trim() === '') {
      const defaultUsers: User[] = [
        {
          id: 'user-1',
          username: 'admin',
          email: 'admin@gongfu.io',
          role: 'admin',
          created: new Date().toISOString()
        },
        {
          id: 'user-2',
          username: 'demo',
          email: 'demo@gongfu.io',
          role: 'user',
          created: new Date().toISOString()
        }
      ];
      await fs.writeJson(this.usersPath, defaultUsers, { spaces: 2 });
    }

    // 确保 sessions 文件存在
    if (!(await fs.pathExists(this.sessionsPath)) || (await fs.readFile(this.sessionsPath, 'utf-8')).trim() === '') {
      await fs.writeJson(this.sessionsPath, [], { spaces: 2 });
    }
  }

  /**
   * 用户登录
   */
  async login(username: string, password: string): Promise<{ user: User; token: string }> {
    // 在实际应用中，这里应该验证密码的哈希值
    // 这里为了简化，只验证用户名和固定密码
    const users = await this.getUsers();
    const user = users.find(u => u.username === username);
    
    if (!user) {
      throw new Error('用户名或密码错误');
    }

    // 简单的密码验证（实际应用中应使用 bcrypt 等）
    const validPasswords: Record<string, string> = {
      'admin': '123456',
      'demo': 'demo123'
    };

    if (password !== validPasswords[username]) {
      throw new Error('用户名或密码错误');
    }

    // 生成 token
    const token = this.generateToken(user.id);
    
    // 创建会话
    const session: AuthSession = {
      token,
      userId: user.id,
      created: new Date().toISOString(),
      expires: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24小时后过期
    };
    
    await this.saveSession(session);

    // 更新最后登录时间
    user.lastLogin = new Date().toISOString();
    await this.updateUser(user);

    logger.info(`User logged in: ${username}`);

    return { user, token };
  }

  /**
   * 验证 token
   */
  async validateToken(token: string): Promise<User | null> {
    const sessions = await this.getSessions();
    const session = sessions.find(s => s.token === token);
    
    if (!session) {
      return null;
    }

    // 检查是否过期
    if (new Date(session.expires) < new Date()) {
      // 删除过期会话
      await this.removeSession(token);
      return null;
    }

    // 获取用户信息
    const users = await this.getUsers();
    const user = users.find(u => u.id === session.userId);
    
    return user || null;
  }

  /**
   * 登出
   */
  async logout(token: string): Promise<void> {
    await this.removeSession(token);
    logger.info('User logged out');
  }

  /**
   * 获取当前用户
   */
  async getCurrentUser(token: string): Promise<User | null> {
    return this.validateToken(token);
  }

  /**
   * 生成 token
   */
  private generateToken(userId: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2);
    const data = `${userId}-${timestamp}-${random}`;
    
    // 创建签名
    const hmac = crypto.createHmac('sha256', this.secretKey);
    hmac.update(data);
    const signature = hmac.digest('hex');
    
    // 返回 base64 编码的 token
    return Buffer.from(`${data}.${signature}`).toString('base64');
  }

  /**
   * 获取所有用户
   */
  private async getUsers(): Promise<User[]> {
    try {
      return await fs.readJson(this.usersPath);
    } catch (error) {
      return [];
    }
  }

  /**
   * 更新用户
   */
  private async updateUser(user: User): Promise<void> {
    const users = await this.getUsers();
    const index = users.findIndex(u => u.id === user.id);
    
    if (index !== -1) {
      users[index] = user;
      await fs.writeJson(this.usersPath, users, { spaces: 2 });
    }
  }

  /**
   * 获取所有会话
   */
  private async getSessions(): Promise<AuthSession[]> {
    try {
      return await fs.readJson(this.sessionsPath);
    } catch (error) {
      return [];
    }
  }

  /**
   * 保存会话
   */
  private async saveSession(session: AuthSession): Promise<void> {
    const sessions = await this.getSessions();
    
    // 移除该用户的旧会话
    const filteredSessions = sessions.filter(s => s.userId !== session.userId);
    filteredSessions.push(session);
    
    await fs.writeJson(this.sessionsPath, filteredSessions, { spaces: 2 });
  }

  /**
   * 移除会话
   */
  private async removeSession(token: string): Promise<void> {
    const sessions = await this.getSessions();
    const filteredSessions = sessions.filter(s => s.token !== token);
    await fs.writeJson(this.sessionsPath, filteredSessions, { spaces: 2 });
  }

  /**
   * 清理过期会话
   */
  async cleanupExpiredSessions(): Promise<void> {
    const sessions = await this.getSessions();
    const now = new Date();
    const activeSessions = sessions.filter(s => new Date(s.expires) > now);
    
    if (activeSessions.length < sessions.length) {
      await fs.writeJson(this.sessionsPath, activeSessions, { spaces: 2 });
      logger.info(`Cleaned up ${sessions.length - activeSessions.length} expired sessions`);
    }
  }
}